import { computed, ref } from "vue";
import { defineStore } from "pinia";
import { $http } from "@/plugins/api";
import { getApiClientUrl, getNewAppClientId } from "@/config/runtimeConfig";
import { isLegacyClientId } from "@/constants/legacyClientId";

export interface AzureAccount {
  id: string | number;
  clientID?: string;
  [key: string]: unknown;
}

export interface OrganisationConnectionStatus {
  organisation_uuid: string;
  connected: boolean;
  client_id?: string;
}

const norm = (value: unknown): string => String(value ?? "").toLowerCase();

export interface NumberOfSeatsPayload {
  organisation: string | null | undefined;
  azureID: string | number | null | undefined;
}

const dedupeById = (
  data: Array<AzureAccount | null | undefined>,
): AzureAccount[] => {
  const idsSeen = new Set<string | number>();

  return (data ?? []).filter((account): account is AzureAccount => {
    if (!account) {
      return false;
    }
    if (idsSeen.has(account.id)) {
      return false;
    }
    idsSeen.add(account.id);
    return true;
  });
};

export const useAzureStore = defineStore("azure", () => {
  const azureAccounts = ref<AzureAccount[]>([]);
  const azureSeats = ref<unknown>(null);
  // Raw connection-status for the selected organisation. This is the single
  // source of truth for which Azure app the customer is connected to: the
  // connection-status endpoint returns the connected app's client_id, and both
  // the SFI migration banner and the M365 connection card derive their state
  // from it. Keeping one signal avoids the two views disagreeing (e.g. the
  // banner still nagging while the card already shows the customer migrated).
  const connectionStatus = ref<OrganisationConnectionStatus | null>(null);

  // Derive the connected Azure app from the connection-status client_id. The
  // legacy app reports either no client_id or the known legacy id; the new app
  // reports the configured new app id. Anything else (or no live connection)
  // is treated as unknown.
  const connectedAzureApp = computed<"old" | "new" | null>(() => {
    const connection = connectionStatus.value;
    if (!connection?.connected) return null;
    if (isLegacyClientId(connection.client_id)) return "old";
    if (norm(connection.client_id) === norm(getNewAppClientId())) return "new";
    return null;
  });

  const isOnLegacyApp = computed(() => connectedAzureApp.value === "old");
  const isOnNewApp = computed(() => connectedAzureApp.value === "new");
  // Backwards-compatible name kept for existing consumers (e.g. the SFI
  // migration banner in CustomerLayout), now derived from the same signal.
  const isLegacyApp = isOnLegacyApp;

  const getAllAzureAccounts = async (
    organisation: string | null | undefined,
  ): Promise<AzureAccount[]> => {
    const res = await $http.get(
      `${getApiClientUrl()}/v1/pt/org/${organisation}/azure`,
      { _hasCustomErrorHandling: true },
    );

    azureAccounts.value = dedupeById(res.data);

    return res.data;
  };

  const getConnectionStatus = async (
    organisation: string | null | undefined,
  ): Promise<void> => {
    if (!organisation) {
      connectionStatus.value = null;
      return;
    }

    const res = await $http.get(
      `${getApiClientUrl()}/v1/pt/organisations/connection-status?uuids=${organisation}`,
      { _hasCustomErrorHandling: true },
    );

    connectionStatus.value = res.data?.results?.[0] ?? null;
  };

  const numberOfSeats = async ({
    organisation,
    azureID,
  }: NumberOfSeatsPayload): Promise<void> => {
    if (azureID) {
      const res = await $http.get(
        `${getApiClientUrl()}/v1/pt/org/${organisation}/seats/${azureID}`,
      );
      azureSeats.value = res.data;
    } else {
      azureSeats.value = undefined;
    }
  };

  const clearAzureCache = (): void => {
    azureAccounts.value = [];
    azureSeats.value = null;
    connectionStatus.value = null;
  };

  return {
    azureAccounts,
    azureSeats,
    connectionStatus,
    isLegacyApp,
    connectedAzureApp,
    isOnLegacyApp,
    isOnNewApp,
    getAllAzureAccounts,
    getConnectionStatus,
    numberOfSeats,
    clearAzureCache,
  };
});
